import React, { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MODULES } from "@/data/course";
import { QUIZZES } from "@/data/quizzes";
import { ArrowLeft, CheckCircle2, XCircle, Trophy, RotateCcw } from "lucide-react";

export default function QuizDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const mid = parseInt(id, 10);
  const module = MODULES.find((m) => m.id === mid);
  const questions = QUIZZES[mid] || [];
  const { setProgress } = useAuth();

  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const [finished, setFinished] = useState(false);

  if (!module) return <div className="text-slate-400">Quiz not found.</div>;

  const q = questions[current];

  const pick = (i) => {
    if (answered) return;
    setSelected(i);
    setAnswered(true);
    if (i === q.answer) setCorrectCount((c) => c + 1);
  };

  const next = async () => {
    if (current + 1 < questions.length) {
      setCurrent((c) => c + 1);
      setSelected(null);
      setAnswered(false);
    } else {
      setFinished(true);
      try {
        const finalCorrect = correctCount;
        const { data } = await api.post("/progress/quiz", { module_id: mid, correct: finalCorrect, total: questions.length });
        setProgress(data);
        toast.success(`Quiz complete! ${finalCorrect}/${questions.length}`);
      } catch {}
    }
  };

  const restart = () => {
    setCurrent(0); setSelected(null); setAnswered(false); setCorrectCount(0); setFinished(false);
  };

  if (finished) {
    const pct = Math.round((correctCount / questions.length) * 100);
    return (
      <div className="max-w-xl mx-auto text-center space-y-6 py-8" data-testid="quiz-result">
        <div className="w-20 h-20 rounded-2xl bg-amber-500/15 border border-amber-500/25 grid place-items-center mx-auto">
          <Trophy className="w-10 h-10 text-amber-400" />
        </div>
        <div>
          <h1 className="font-display text-3xl font-black">Quiz complete!</h1>
          <p className="text-slate-400 mt-1">Module {mid} — {module.title}</p>
        </div>
        <div className="font-mono text-5xl font-bold text-blue-400">{correctCount}/{questions.length}</div>
        <p className="text-slate-300">{pct === 100 ? "Perfect score! 🎉" : pct >= 66 ? "Great job — keep going!" : "Good effort. Review the module and try again!"}</p>
        <div className="flex justify-center gap-3">
          <button onClick={restart} data-testid="quiz-retry" className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg px-5 py-2.5 text-sm font-semibold transition-colors"><RotateCcw className="w-4 h-4" /> Retry</button>
          <button onClick={() => navigate("/quizzes")} data-testid="quiz-back" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg px-5 py-2.5 text-sm font-semibold transition-colors">All quizzes</button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <Link to="/quizzes" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-200"><ArrowLeft className="w-4 h-4" /> Quizzes</Link>
      <div>
        <div className="flex items-center justify-between text-xs font-mono text-slate-500 mb-2">
          <span>Module {mid} · {module.title}</span>
          <span>Question {current + 1} / {questions.length}</span>
        </div>
        <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
          <div className="h-full bg-blue-500 transition-all" style={{ width: `${((current) / questions.length) * 100}%` }} />
        </div>
      </div>

      <div className="bg-[#0F172A] border border-slate-800 rounded-2xl p-6">
        <h2 className="font-display text-xl font-semibold mb-5" data-testid="quiz-question">{q.q}</h2>
        <div className="space-y-3">
          {q.options.map((opt, i) => {
            const isCorrect = i === q.answer;
            const isSelected = selected === i;
            let cls = "border-slate-700 bg-slate-900/60 hover:border-blue-500/50";
            if (answered) {
              if (isCorrect) cls = "border-emerald-500/60 bg-emerald-500/10";
              else if (isSelected) cls = "border-red-500/60 bg-red-500/10";
              else cls = "border-slate-800 bg-slate-900/40 opacity-60";
            }
            return (
              <button key={i} data-testid={`quiz-option-${i}`} onClick={() => pick(i)} disabled={answered}
                className={`w-full text-left flex items-center justify-between gap-3 rounded-xl border px-4 py-3 text-sm transition-all ${cls}`}>
                <span className="text-slate-200">{opt}</span>
                {answered && isCorrect && <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />}
                {answered && isSelected && !isCorrect && <XCircle className="w-5 h-5 text-red-400 shrink-0" />}
              </button>
            );
          })}
        </div>

        {answered && (
          <div className="mt-5 bg-blue-500/10 border border-blue-500/25 rounded-xl p-4 text-sm text-slate-200 animate-in" data-testid="quiz-explanation">
            <span className="font-semibold text-blue-300">Explanation: </span>{q.explain}
          </div>
        )}

        {answered && (
          <button onClick={next} data-testid="quiz-next" className="mt-5 w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-lg py-2.5 transition-colors">
            {current + 1 < questions.length ? "Next question" : "See results"}
          </button>
        )}
      </div>
    </div>
  );
}
